// src/components/dashboard/RemoveMemberOptionsModal.js
// Updated: 10/20/2025 08:15 PM

import React, { useState } from 'react';
import ModalWrapper from '../common/ModalWrapper';
import Spinner from '../common/Spinner';
import { FaTrash, FaUserSlash, FaExclamationTriangle } from 'react-icons/fa';

const RemoveMemberOptionsModal = ({ member, onConfirm, onCancel, zIndexClass }) => {
  const [loading, setLoading] = useState(false);
  const [option, setOption] = useState(null); // 'detach' or 'remove'

  const handleConfirm = async () => {
    if (!option) {
        alert("Please select an option."); // Basic validation
        return;
    }
    setLoading(true);
    try {
      // Pass the chosen option back to the parent handler
      await onConfirm(option === 'detach'); // Pass true if 'detach' (keep record), false if 'remove'
    } catch (e) {
      // Error handled by caller's toast
    } finally {
      // Don't setLoading(false) here if onConfirm closes the modal
      // setLoading(false);
    }
  };

  if (!member) return null;

  return (
    <ModalWrapper onClose={onCancel} size="md" zIndexClass={zIndexClass}>
      <div className="p-6 border-b">
        <h3 className="text-xl font-semibold text-neutral-gray">Remove {member.firstName} {member.lastName}?</h3>
      </div>
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-600">
            How would you like to remove this member from the family group?
        </p>

        {/* Option 1: Detach Account, Keep Record */}
        <div className={`p-3 border rounded-md cursor-pointer ${option === 'detach' ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' : 'hover:bg-gray-50'}`} onClick={() => setOption('detach')}>
            <label htmlFor="detachOption" className="flex items-center gap-3 cursor-pointer">
                <input type="radio" id="detachOption" name="removeOption" value="detach" checked={option === 'detach'} readOnly className="focus:ring-primary h-4 w-4 text-primary border-gray-300"/>
                <div>
                    <span className="font-semibold text-blue-800 flex items-center gap-2"><FaUserSlash /> Remove Account, Keep Tree Record</span>
                    <p className="text-xs text-blue-700 mt-1">
                        Removes the user's account access from this family, but creates a manual copy to preserve their position and links in the Family Tree. Useful if they might rejoin later or you need the historical structure.
                    </p>
                </div>
            </label>
        </div>

        {/* Option 2: Remove Completely */}
        <div className={`p-3 border rounded-md cursor-pointer ${option === 'remove' ? 'bg-red-50 border-red-300 ring-2 ring-red-200' : 'hover:bg-gray-50'}`} onClick={() => setOption('remove')}>
             <label htmlFor="removeOption" className="flex items-center gap-3 cursor-pointer">
                <input type="radio" id="removeOption" name="removeOption" value="remove" checked={option === 'remove'} readOnly className="focus:ring-red-500 h-4 w-4 text-red-600 border-gray-300"/>
                <div>
                    <span className="font-semibold text-red-800 flex items-center gap-2"><FaTrash /> Remove Completely</span>
                     <p className="text-xs text-red-700 mt-1">
                        Completely removes the user's account access from this family AND removes their record from the Family Tree. Relationship links (spouse, parent) on other members pointing to this user will be cleared.
                    </p>
                </div>
            </label>
        </div>

         <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded-md text-yellow-800 text-sm flex items-start gap-2">
            <FaExclamationTriangle className="mt-0.5 flex-shrink-0"/>
            <span>
                This action detaches the user's account from this family. If preserving the record, a manual copy is made. Proceed with caution.
            </span>
         </div>
      </div>
      <div className="p-6 bg-gray-50 rounded-b-lg flex justify-end items-center gap-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading || !option} // Disable if no option selected
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? <Spinner size="sm" /> : <>Confirm Removal</>}
        </button>
      </div>
    </ModalWrapper>
  );
};

export default RemoveMemberOptionsModal;